import autocomplete from "./autocompleter";

const FOCUS_STORAGE_KEY = "ntry-focus-event-search";
const MISSING_SEARCH_IMAGE_URL = "/event_images/list_size/missing.png";
const VIEWPORT_PADDING = 12;

function renderSearchResult(data) {
  const div = document.createElement("div");
  div.className = "search-entry";

  const image = document.createElement("img");
  image.className = "search-entry-image";
  image.src = data.image_url || MISSING_SEARCH_IMAGE_URL;
  image.alt = data.title ? `Event image for ${data.title}` : "";
  image.onerror = function() {
    this.onerror = null;
    this.src = MISSING_SEARCH_IMAGE_URL;
  };
  div.appendChild(image);

  const body = document.createElement("div");
  body.className = "search-entry-body";
  div.appendChild(body);

  const title = document.createElement("div");
  title.className = "search-entry-title";
  title.textContent = data.title;
  body.appendChild(title);

  if (data.performance_title) {
    const performanceTitle = document.createElement("div");
    performanceTitle.className = "search-entry-subtitle";
    performanceTitle.textContent = data.performance_title;
    body.appendChild(performanceTitle);
  }

  if (data.date) {
    const date = document.createElement("div");
    date.className = "search-entry-meta";

    const icon = document.createElement("i");
    icon.className = "fa-solid fa-calendar";
    date.appendChild(icon);

    const text = document.createElement("span");
    text.textContent = data.date;
    date.appendChild(text);

    body.appendChild(date);
  }

  if (data.location) {
    const location = document.createElement("div");
    location.className = "search-entry-meta";

    const icon = document.createElement("i");
    icon.className = "fa-solid fa-map-marker";
    location.appendChild(icon);

    const text = document.createElement("span");
    text.textContent = data.location;
    location.appendChild(text);

    body.appendChild(location);
  }

  return div;
}

function fetchSearchResults(text, update) {
  const query = text.trim().toLowerCase();

  fetch(`/ajax/search?q=${encodeURIComponent(query)}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Search request failed with status ${response.status}`);
      }

      return response.json();
    })
    .then((data) => update(Array.isArray(data) ? data : []))
    .catch((error) => {
      console.error(error);
      update([]);
    });
}

function customizeDropdown(input, inputRect, container, maxHeight) {
  const pageXOffset = window.pageXOffset || document.documentElement.scrollLeft;
  const pageYOffset = window.pageYOffset || document.documentElement.scrollTop;
  const availableHeight = Number.isFinite(maxHeight)
    ? maxHeight
    : window.innerHeight - inputRect.bottom;
  const dropdownMaxHeight = Math.min(Math.max(0, availableHeight - VIEWPORT_PADDING), 680);

  container.style.left = `${inputRect.left + pageXOffset}px`;
  container.style.top = `${inputRect.bottom + pageYOffset}px`;
  container.style.width = `${inputRect.width}px`;
  container.style.maxHeight = `${dropdownMaxHeight}px`;
}

function initEventSearch(searchBox) {
  if (searchBox.dataset.eventSearchInitialized === "true") {
    return;
  }

  const queryInput = searchBox.querySelector("[data-event-search-input]");
  if (!queryInput) {
    return;
  }

  const emptyMsg = searchBox.dataset.eventSearchEmptyMessage || "No event found.";

  searchBox.dataset.eventSearchInitialized = "true";

  autocomplete({
    input: queryInput,
    className: "search-autocomplete",
    disableAutoSelect: true,
    fetch: fetchSearchResults,
    render: renderSearchResult,
    customize: customizeDropdown,
    onSelect(selection) {
      window.location.href = selection.url;
    },
    minLength: 2,
    emptyMsg
  });
}

function focusEventSearch({ immediate = false } = {}) {
  const queryInput = document.querySelector("[data-event-search-input]");
  if (!queryInput) {
    return;
  }

  if (immediate) {
    queryInput.focus();
    return;
  }

  window.setTimeout(() => {
    queryInput.focus();
  }, 0);
}

function initEventSearches() {
  document.querySelectorAll("[data-event-search]").forEach(initEventSearch);

  if (consumeFocusRequest()) {
    focusEventSearch();
  }
}

function storeFocusRequest() {
  try {
    window.sessionStorage.setItem(FOCUS_STORAGE_KEY, "true");
  } catch {
    // Navigation still works if storage is blocked; only autofocus is skipped.
  }
}

function consumeFocusRequest() {
  try {
    if (window.sessionStorage.getItem(FOCUS_STORAGE_KEY) !== "true") {
      return false;
    }

    window.sessionStorage.removeItem(FOCUS_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

document.addEventListener("DOMContentLoaded", initEventSearches);

document.addEventListener("click", (event) => {
  const link = event.target.closest("[data-event-search-nav-link]");
  if (!link) {
    return;
  }

  const url = new URL(link.href, window.location.href);
  if (url.pathname === window.location.pathname && url.search === window.location.search) {
    event.preventDefault();
    focusEventSearch({ immediate: true });
  } else {
    storeFocusRequest();
  }
});
